import { Injectable, OnDestroy, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from 'aws-amplify';
import { SnackbarService } from '../snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class AuthMonitorService implements OnDestroy {
  private checkInterval: any;
  private timeoutId: any;
  private refreshTokenExpiry: Date | null = null;
  
  constructor(private router: Router, private snackbar: SnackbarService) {
    this.startMonitor();
  }

  async startMonitor() {
    this.devLog('AuthMonitor loading');
    this.startTokenMonitor();
    this.setRefreshTokenExpiry();
  }

  async stopMonitor() {
    this.devLog('Stopping auth monitor');
    this.clearHandles();
  }

  async reLoad() {
    this.devLog('Reloading auth monitor');
    this.clearHandles();
    this.startMonitor();
  }

  private clearHandles() {
    this.devLog('Clearing monitor handles');
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  /**
   * Start periodic token monitoring
   */
  private startTokenMonitor(): void {
    this.devLog('monitoring token');
    const fifteenMinutes = 15 * 60 * 1000;
    this.checkInterval = setInterval(() => this.checkTokenValidity(), fifteenMinutes); 
  }

  /**
   * Fetch and calculate the refresh token's expiration time   
   */
  private async setRefreshTokenExpiry(): Promise<void> {
    try {

      if (this.timeoutId) {
        clearTimeout(this.timeoutId); //Set prior, clear it
      }

      // Wait a bit to ensure session is properly set
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const session = await Auth.currentSession();
      const authenticationTime = session.getIdToken().getIssuedAt(); // Auth time (in seconds)
      
      //Refresh token set by app client, this one is for ClientPortal
      //DEV: https://us-east-1.console.aws.amazon.com/cognito/v2/idp/user-pools/us-east-1_i3IEVfPeF/applications/app-clients/b8vr7fu9nfjjr65p90d76gruj/quick-setup-guide?region=us-east-1
      //PROD: https://us-east-1.console.aws.amazon.com/cognito/v2/idp/user-pools/us-east-1_0f0oa7Hna/applications/app-clients/7107sbfp1719a24u0svvqcd68m/quick-setup-guide?region=us-east-1
      // Refresh token TTL is determined by the app client configuration in Cognito.
      // For reference, see your app client settings in the AWS Console under User Pools -> Applications -> App clients.
      const refreshTokenTTL = 24 * 60 * 60; // 1 day in seconds

      // Calculate refresh token expiration
      this.refreshTokenExpiry = new Date((authenticationTime + refreshTokenTTL) * 1000);

      this.devLog(`Refresh token will expire on: ${this.refreshTokenExpiry}`);
      this.scheduleRefreshTokenWarning();
    } catch (error) {
      console.error('Error fetching session to determine refresh token expiry:', error);
    }
  }

  /**
   * Schedule a warning 15 minutes before the refresh token expires
   */
  private scheduleRefreshTokenWarning(): void {
    if (!this.refreshTokenExpiry) return;
    const fifteenMinutes = 15 * 60 * 1000; // 15 minutes before expiry
    const warningTime = this.refreshTokenExpiry.getTime() - fifteenMinutes; 
    const currentTime = Date.now();
    const delay = warningTime - currentTime;

    if (delay > 0) {
      this.devLog(`Warning will be shown in ${delay / 1000 / 60} minutes.`);
      this.timeoutId = setTimeout(() => {
        this.showRefreshTokenWarning();
      }, delay);
    } else {
      console.warn('Warning time has already passed. Token may be expired.');
    }
  }

  /**
   * Show a warning to the user about refresh token expiration
   */
  private showRefreshTokenWarning(): void {
    const msg = 'Your session is about to expire.  Click profile icon at upper right and select "Sign Out" to log out and log back in again.';
    console.warn(msg);
    this.snackbar.showMessageSnackBar(msg, 'center', 'top');
  }

  /**
   * Check if the user's token is still valid
   */
  private async checkTokenValidity(): Promise<void> {
    try {
      this.devLog('checking token');
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
  
      if (this.isTokenExpired(token)) {
        console.warn('Token expired.');
  
        // Avoid redirecting if already on the signin page or handling a retry
        if (!this.router.url.startsWith('/signin')) {
          this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
        }
      } else {
        this.devLog("Not expired, remaining time:", this.formatRemainingTime(this.getRemainingTime(token)));
        
      }
    } catch (error) {
      console.error('Failed to fetch session or token expired.', error);
  
      // Avoid redirecting if already on the signin page or handling a retry
      if (!this.router.url.startsWith('/signin')) {
        this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
      }
    }
  }
  

  /**
   * Decode token and check expiration
   * @param token - JWT token
   * @returns boolean
   */
  private isTokenExpired(token: string): boolean {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    console.debug(payload, currentTime, payload.exp < currentTime);
    return payload.exp < currentTime; // Check if token is expired
  }


  /**
   * Calculate remaining time until token expiration
   * @param token - JWT token
   * @returns remaining time in seconds
   */
  private getRemainingTime(token: string): number {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp; // Expiration time in seconds (Unix epoch)
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return expirationTime - currentTime; // Time remaining in seconds
  }

  private formatRemainingTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  // Debug log if in dev mode only
  devLog(message: string, object?:any) {
    if (isDevMode()) {
      console.debug(message, object);
    }    
  }


  /**
   * Clean up when the service is destroyed
   */
  ngOnDestroy(): void {
    this.clearHandles();
  }
}
